import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';

const ScrollableContext = createContext(null);

export const useScrollable = () => {
  const ctx = useContext(ScrollableContext);
  if (!ctx) throw new Error('useScrollable must be used inside ScrollableProvider');
  return ctx;
};

const EPSILON = 1; // tolerance for subpixel rounding

export const ScrollableProvider = ({
  children,
  horizontal = false,
  vertical = false,
  viewportW,
  viewportH,
  scrollStep = 200,
}) => {
  const contentRef = useRef(null);

  const [canScrollNext, setCanScrollNext] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [isHorizontalOverflowing, setIsHorizontalOverflowing] = useState(false);
  const [isVerticalOverflowing, setIsVerticalOverflowing] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;

    // ---- Overflow detection (epsilon-safe)
    if (horizontal) {
      const overflowing = el.scrollWidth - el.clientWidth > EPSILON;
      setIsHorizontalOverflowing(overflowing);
    } else {
      setIsHorizontalOverflowing(false);
    }

    if (vertical) {
      const overflowing = el.scrollHeight - el.clientHeight > EPSILON;
      setIsVerticalOverflowing(overflowing);
    } else {
      setIsVerticalOverflowing(false);
    }

    // ---- Shared canScroll flags (vertical takes precedence)
    if (vertical) {
      const remaining = Math.max(
        0,
        el.scrollHeight - el.scrollTop - el.clientHeight
      );
      setCanScrollPrev(el.scrollTop > EPSILON);
      setCanScrollNext(remaining > EPSILON);
    } else if (horizontal) {
      const remaining = Math.max(
        0,
        el.scrollWidth - el.scrollLeft - el.clientWidth
      );
      setCanScrollPrev(el.scrollLeft > EPSILON);
      setCanScrollNext(remaining > EPSILON);
    } else {
      setCanScrollPrev(false);
      setCanScrollNext(false);
    }
  }, [horizontal, vertical]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    updateScrollState();

    el.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateScrollState);
      resizeObserver.observe(el);
    }

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [updateScrollState]);

  const scrollNext = () => {
    const el = contentRef.current;
    if (!el) return;

    el.scrollBy({
      left: horizontal ? scrollStep : 0,
      top: vertical ? scrollStep : 0,
      behavior: 'smooth',
    });
  };

  const scrollPrev = () => {
    const el = contentRef.current;
    if (!el) return;

    el.scrollBy({
      left: horizontal ? -scrollStep : 0,
      top: vertical ? -scrollStep : 0,
      behavior: 'smooth',
    });
  };

  return (
    <ScrollableContext.Provider
      value={{
        contentRef,
        scrollNext,
        scrollPrev,
        canScrollNext,
        canScrollPrev,
        isHorizontalOverflowing,
        isVerticalOverflowing,
        horizontal,
        vertical,
        viewportW,
        viewportH,
      }}
    >
      {children}
    </ScrollableContext.Provider>
  );
};